import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchBookingGroupInfo, fetchPayPage, startCheckout } from "@/lib/payApi";
import { ArrowLeft, FileDown } from "lucide-react";
import { buildInvoicePdf, downloadBlob } from "@/lib/invoice";
import {
  formatDisplayDate,
  formatTime,
  PAYMENT_STATUS_LABELS,
} from "@/lib/bookingLabels";
import { formatGbpFromPence, type BookingPayPage } from "@/types/database";

type UiState =
  | "loading"
  | "ready"
  | "already_paid"
  | "cancelled_booking"
  | "checkout_cancelled"
  | "confirming"
  | "error"
  | "unconfigured";

const BookingPay = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const statusQ = params.get("status");

  const [booking, setBooking] = useState<BookingPayPage | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [ui, setUi] = useState<UiState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [groupInfo, setGroupInfo] = useState<{ group_token: string; group_count: number } | null>(null);
  useEffect(() => {
    void fetchBookingGroupInfo(token).then(setGroupInfo);
  }, [token]);

  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };

  const load = useCallback(async () => {
    const res = await fetchPayPage(token);
    if (res.ok === false) {
      setBooking(null);
      setError(res.error);
      setUi(res.unconfigured ? "unconfigured" : "error");
      return null;
    }
    setBooking(res.data);
    setError(null);
    if (res.data.booking_status === "cancelled") {
      setUi("cancelled_booking");
    } else if (res.data.payment_status === "paid") {
      setUi("already_paid");
    } else if (statusQ === "success") {
      setUi("confirming");
    } else if (statusQ === "cancelled") {
      setUi("checkout_cancelled");
    } else {
      setUi("ready");
    }
    return res.data;
  }, [token, statusQ]);

  useEffect(() => {
    document.title = "Pay securely · Olivia Flemming";
    void load();
  }, [load]);

  // After Stripe redirect: poll until webhook marks paid (never trust redirect alone)
  useEffect(() => {
    if (ui !== "confirming") return;
    let tries = 0;
    const id = window.setInterval(async () => {
      tries += 1;
      const res = await fetchPayPage(token);
      if (res.ok && res.data.payment_status === "paid") {
        setBooking(res.data);
        setUi("already_paid");
        window.clearInterval(id);
      } else if (tries >= 15) {
        window.clearInterval(id);
        setError(
          "We’re still confirming your payment. If you were charged, you’re all set — Olivia will see it shortly. You can refresh this page."
        );
      }
    }, 2000);
    return () => window.clearInterval(id);
  }, [ui, token]);

  const onPay = async () => {
    setPaying(true);
    setError(null);
    const res = await startCheckout(token);
    setPaying(false);
    if (res.ok === false) {
      if (res.code === "already_paid") {
        setUi("already_paid");
        await load();
        return;
      }
      if (res.code === "cancelled") {
        setUi("cancelled_booking");
        return;
      }
      setError(res.error);
      return;
    }
    window.location.href = res.data.url;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="font-playfair tracking-widest text-sm">
            OLIVIA FLEMMING
          </Link>
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-inter">
            Secure payment
          </span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-10 md:py-14">
        <button
          type="button"
          onClick={goBack}
          className="mb-4 inline-flex items-center gap-2 text-sm font-inter text-white/50 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {ui === "loading" && (
          <p className="text-white/50 text-sm font-inter text-center">Loading booking…</p>
        )}

        {ui === "unconfigured" && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-sm text-amber-100/90 font-inter">
            Payment page is ready in code, but Supabase is not configured in this environment yet.
          </div>
        )}

        {ui === "error" && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center">
            <h1 className="font-playfair text-2xl mb-3">Link unavailable</h1>
            <p className="text-sm text-white/70 font-inter">{error}</p>
          </div>
        )}

        {booking && ui !== "loading" && ui !== "error" && ui !== "unconfigured" && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3 font-inter">
                Olivia Flemming
              </p>
              <h1 className="font-playfair text-3xl md:text-4xl mb-2">
                {ui === "already_paid"
                  ? "Payment received"
                  : ui === "cancelled_booking"
                    ? "Booking cancelled"
                    : ui === "confirming"
                      ? "Confirming payment"
                      : "Pay securely"}
              </h1>
              <p className="text-white/50 text-sm font-inter">{booking.reference}</p>
            </div>

            {groupInfo && (
              <Link
                to={`/booking/pay-group/${groupInfo.group_token}`}
                className="block rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-inter text-center text-primary hover:bg-primary/15"
              >
                This is 1 of your {groupInfo.group_count} performances. See them all and pay together
              </Link>
            )}

            <div className="rounded-2xl border border-white/10 bg-secondary/60 p-6 space-y-4 font-inter text-sm">
              <Row label="Venue / client" value={booking.venue_or_client_name} />
              <Row
                label="Date"
                value={formatDisplayDate(booking.performance_date)}
              />
              <Row
                label="Time"
                value={
                  formatTime(booking.start_time) === "—"
                    ? "—"
                    : `${formatTime(booking.start_time)}${
                        booking.end_time ? ` to ${formatTime(booking.end_time)}` : ""
                      }`
                }
              />
              <Row
                label="Location"
                value={[booking.address, booking.venue_postcode].filter(Boolean).join(", ") || "—"}
              />
              <Row
                label="Payment due by"
                value={booking.due_date ? formatDisplayDate(booking.due_date) : "—"}
              />
              {booking.client_description && (
                <Row label="Details" value={booking.client_description} />
              )}
              {(booking.fuel_charge_pence ?? 0) > 0 && (
                <div className="flex justify-between text-white/70">
                  <span>
                    Performance {formatGbpFromPence(booking.fee_pence - (booking.fuel_charge_pence ?? 0))}
                  </span>
                  <span>
                    Travel {formatGbpFromPence(booking.fuel_charge_pence ?? 0)}
                    {booking.distance_miles ? ` (${booking.distance_miles} mi)` : ""}
                  </span>
                </div>
              )}
              <div className="pt-2 border-t border-white/10 flex items-end justify-between">
                <span className="text-white/45 text-xs uppercase tracking-wider">Total</span>
                <span className="font-playfair text-3xl text-white">
                  {formatGbpFromPence(booking.fee_pence)}
                </span>
              </div>
            </div>

            {ui === "already_paid" && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100 font-inter text-center">
                Thank you — this booking is marked{" "}
                <strong>{PAYMENT_STATUS_LABELS.paid}</strong>. A confirmation email will follow once
                email (Stage 6) is connected.
              </div>
            )}

            {ui === "cancelled_booking" && (
              <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-4 text-sm text-white/60 font-inter text-center">
                This booking has been cancelled and cannot be paid.
              </div>
            )}

            {ui === "confirming" && (
              <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-4 text-sm text-primary font-inter text-center">
                Thanks — confirming with Stripe now (we never rely on the redirect alone)…
              </div>
            )}

            {ui === "checkout_cancelled" && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm text-amber-100/90 font-inter text-center">
                Checkout was cancelled. You can try again when ready.
              </div>
            )}

            {error && (
              <p className="text-sm text-red-300 font-inter text-center" role="alert">
                {error}
              </p>
            )}

            {booking.due_date && (ui === "ready" || ui === "checkout_cancelled") && (
              <p className="text-center text-sm font-inter text-primary">
                Payment due by {formatDisplayDate(booking.due_date)}
              </p>
            )}

            {(ui === "ready" || ui === "checkout_cancelled") && (
              <button
                type="button"
                onClick={onPay}
                disabled={paying}
                className="w-full rounded-full bg-primary text-black font-medium py-4 text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {paying ? "Redirecting to Stripe…" : "Pay Securely"}
              </button>
            )}

            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  buildInvoicePdf({
                    invoiceNo: booking.reference,
                    clientName: booking.venue_or_client_name,
                    contactName: booking.contact_name,
                    clientEmail: booking.client_email,
                    dueDate: booking.due_date,
                    lines: [{ ...booking, address: [booking.address, booking.venue_postcode].filter(Boolean).join(", "), paid: booking.payment_status === "paid" }],
                  }),
                  `Invoice ${booking.reference}.pdf`
                )
              }
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 py-3.5 text-sm font-inter text-white/80 hover:border-primary hover:text-primary transition-colors"
            >
              <FileDown className="w-4 h-4" /> Download invoice (PDF)
            </button>

            <p className="text-center text-[11px] text-white/35 font-inter leading-relaxed">
              Card payments are processed by Stripe. Olivia never sees your card details.
              Apple Pay / Google Pay appear when supported by Stripe Checkout.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">{label}</p>
      <p className="text-white/85 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export default BookingPay;
